/**
 * Role-based access control configuration for Chairside Frontend
 * This mirrors the backend roleScopes.js configuration
 */

export const ROLES = {
  SUPER_ADMIN: "super_admin",
  OWNER: "owner",
  TREATMENT_COORDINATOR: "treatment_coordinator",
  FRONT_DESK: "front_desk",
  AGENT: "agent",
  TEAM_LEADER: "team_leader",
};

export const PERMISSIONS = {
  // Super Admin Only
  MANAGE_ALL_ORGANIZATIONS: "manage_all_organizations",
  VIEW_ALL_ORGANIZATIONS: "view_all_organizations",
  MANAGE_SUPER_ADMINS: "manage_super_admins",
  VIEW_PLATFORM_STATS: "view_platform_stats",

  // Organization & Location
  MANAGE_ORGANIZATION: "manage_organization",
  MANAGE_LOCATIONS: "manage_locations",
  MANAGE_USERS: "manage_users",
  VIEW_ALL_LOCATIONS: "view_all_locations",

  // PMS Integration
  MANAGE_PMS: "manage_pms",
  VIEW_APPOINTMENTS: "view_appointments",

  // Consultations
  START_CONSULTATION: "start_consultation",
  CONTINUE_CONSULTATION: "continue_consultation",
  VIEW_CONSULTATIONS: "view_consultations",
  VIEW_OWN_CONSULTATIONS: "view_own_consultations",

  // Recordings & Transcriptions
  VIEW_RECORDINGS: "view_recordings",
  VIEW_TRANSCRIPTIONS: "view_transcriptions",

  // SOAP Notes
  CREATE_SOAP_NOTES: "create_soap_notes",
  EDIT_SOAP_NOTES: "edit_soap_notes",
  APPROVE_SOAP_NOTES: "approve_soap_notes",
  VIEW_SOAP_NOTES: "view_soap_notes",

  // Follow-ups & Conversations
  VIEW_FOLLOW_UPS: "view_follow_ups",
  MANAGE_FOLLOW_UPS: "manage_follow_ups",
  VIEW_CONVERSATIONS: "view_conversations",

  // Tasks
  VIEW_ALL_TASKS: "view_all_tasks",
  VIEW_OWN_TASKS: "view_own_tasks",
  RESPOND_TO_TASKS: "respond_to_tasks",
  CREATE_TASKS: "create_tasks",

  // Scheduling
  VIEW_SCHEDULING_TASKS: "view_scheduling_tasks",
  CONFIRM_SCHEDULING: "confirm_scheduling",

  // Reactivation
  VIEW_REACTIVATION: "view_reactivation",
  MANAGE_REACTIVATION: "manage_reactivation",

  // Coaching
  VIEW_OWN_COACHING: "view_own_coaching",
  VIEW_ALL_COACHING: "view_all_coaching",

  // Analytics
  VIEW_ANALYTICS: "view_analytics",
  VIEW_TEAM_ANALYTICS: "view_team_analytics",

  // Pipeline
  VIEW_PIPELINE: "view_pipeline",

  // Settings
  MANAGE_SETTINGS: "manage_settings",

  // Treatments
  VIEW_TREATMENTS: "view_treatments",
  MANAGE_TREATMENTS: "manage_treatments",

  // Stages
  VIEW_STAGES: "view_stages",
  MANAGE_STAGES: "manage_stages",

  // Tickets / Support
  CREATE_TICKET: "create_ticket",
  VIEW_ORG_TICKETS: "view_org_tickets",
  VIEW_OWN_TICKETS: "view_own_tickets",
  MANAGE_ALL_TICKETS: "manage_all_tickets",

  // Agent management
  MANAGE_AGENTS: "manage_agents",

  // Recare Dashboard
  VIEW_RECARE_DASHBOARD: "view_recare_dashboard",
};

/**
 * Role permission mappings based on PRD Role Visibility Matrix
 */
export const ROLE_PERMISSIONS = {
  [ROLES.SUPER_ADMIN]: [
    // Full access to EVERYTHING across ALL organizations
    ...Object.values(PERMISSIONS),
  ],

  [ROLES.OWNER]: [
    // Full access to their own organization (excluding super admin + location management)
    ...Object.values(PERMISSIONS).filter(
      (p) =>
        p !== PERMISSIONS.MANAGE_ALL_ORGANIZATIONS &&
        p !== PERMISSIONS.VIEW_ALL_ORGANIZATIONS &&
        p !== PERMISSIONS.MANAGE_SUPER_ADMINS &&
        p !== PERMISSIONS.VIEW_PLATFORM_STATS &&
        p !== PERMISSIONS.MANAGE_LOCATIONS,
    ),
  ],

  [ROLES.TREATMENT_COORDINATOR]: [
    PERMISSIONS.VIEW_APPOINTMENTS,
    PERMISSIONS.START_CONSULTATION,
    PERMISSIONS.CONTINUE_CONSULTATION,
    PERMISSIONS.VIEW_CONSULTATIONS,
    PERMISSIONS.VIEW_OWN_CONSULTATIONS,
    PERMISSIONS.VIEW_RECORDINGS,
    PERMISSIONS.VIEW_TRANSCRIPTIONS,
    PERMISSIONS.CREATE_SOAP_NOTES,
    PERMISSIONS.EDIT_SOAP_NOTES,
    PERMISSIONS.VIEW_SOAP_NOTES,
    PERMISSIONS.VIEW_FOLLOW_UPS,
    PERMISSIONS.MANAGE_FOLLOW_UPS,
    PERMISSIONS.VIEW_CONVERSATIONS,
    PERMISSIONS.VIEW_ALL_TASKS,
    PERMISSIONS.VIEW_OWN_TASKS,
    PERMISSIONS.RESPOND_TO_TASKS,
    PERMISSIONS.VIEW_OWN_COACHING,
    PERMISSIONS.VIEW_SCHEDULING_TASKS,
    PERMISSIONS.CONFIRM_SCHEDULING,
    PERMISSIONS.VIEW_PIPELINE,
    PERMISSIONS.VIEW_TREATMENTS,
    PERMISSIONS.MANAGE_TREATMENTS,
    PERMISSIONS.VIEW_STAGES,
    PERMISSIONS.MANAGE_STAGES,
    PERMISSIONS.CREATE_TICKET,
    PERMISSIONS.VIEW_OWN_TICKETS,
  ],

  [ROLES.FRONT_DESK]: [
    PERMISSIONS.VIEW_APPOINTMENTS,
    PERMISSIONS.VIEW_CONSULTATIONS,
    PERMISSIONS.VIEW_ALL_TASKS,
    PERMISSIONS.RESPOND_TO_TASKS,
    PERMISSIONS.VIEW_SCHEDULING_TASKS,
    PERMISSIONS.CONFIRM_SCHEDULING,
    PERMISSIONS.VIEW_CONVERSATIONS,
    PERMISSIONS.VIEW_FOLLOW_UPS,
    PERMISSIONS.MANAGE_FOLLOW_UPS,
    PERMISSIONS.VIEW_PIPELINE,
    PERMISSIONS.VIEW_TREATMENTS,
    PERMISSIONS.CREATE_TICKET,
    PERMISSIONS.VIEW_OWN_TICKETS,
  ],

  [ROLES.AGENT]: [
    PERMISSIONS.VIEW_APPOINTMENTS,
    PERMISSIONS.VIEW_CONSULTATIONS,
    PERMISSIONS.VIEW_FOLLOW_UPS,
    PERMISSIONS.MANAGE_FOLLOW_UPS,
    PERMISSIONS.VIEW_CONVERSATIONS,
    PERMISSIONS.VIEW_ALL_TASKS,
    PERMISSIONS.RESPOND_TO_TASKS,
    PERMISSIONS.VIEW_SCHEDULING_TASKS,
    PERMISSIONS.CONFIRM_SCHEDULING,
    PERMISSIONS.VIEW_PIPELINE,
    PERMISSIONS.VIEW_TREATMENTS,
  ],

  [ROLES.TEAM_LEADER]: [
    PERMISSIONS.MANAGE_AGENTS,
    PERMISSIONS.VIEW_APPOINTMENTS,
    PERMISSIONS.VIEW_CONSULTATIONS,
    PERMISSIONS.VIEW_FOLLOW_UPS,
    PERMISSIONS.MANAGE_FOLLOW_UPS,
    PERMISSIONS.VIEW_CONVERSATIONS,
    PERMISSIONS.VIEW_ALL_TASKS,
    PERMISSIONS.RESPOND_TO_TASKS,
    PERMISSIONS.CREATE_TASKS,
    PERMISSIONS.VIEW_SCHEDULING_TASKS,
    PERMISSIONS.CONFIRM_SCHEDULING,
    PERMISSIONS.VIEW_PIPELINE,
    PERMISSIONS.VIEW_TREATMENTS,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_TEAM_ANALYTICS,
  ],
};

/**
 * Check if a role has a specific permission
 * @param {string} role - User's role
 * @param {string|string[]} permission - Permission(s) to check
 * @param {boolean} requireAll - If true, must have ALL permissions (AND). Default is false (OR)
 * @returns {boolean}
 */
export const hasPermission = (role, permission, requireAll = false) => {
  if (!role) return false;

  // Super admin has ALL permissions
  if (role === ROLES.SUPER_ADMIN) return true;

  const permissions = ROLE_PERMISSIONS[role] || [];

  if (Array.isArray(permission)) {
    return requireAll
      ? permission.every((p) => permissions.includes(p))
      : permission.some((p) => permissions.includes(p));
  }

  return permissions.includes(permission);
};

/**
 * Check if role is super admin
 * @param {string} role - User's role
 * @returns {boolean}
 */
export const isSuperAdmin = (role) => {
  return role === ROLES.SUPER_ADMIN;
};

/**
 * Get all permissions for a role
 * @param {string} role - User's role
 * @returns {string[]}
 */
export const getRolePermissions = (role) => {
  return ROLE_PERMISSIONS[role] || [];
};

/**
 * Check if user can access a feature based on multiple permissions (OR logic)
 * @param {string} role - User's role
 * @param {string[]} permissions - Array of permissions (user needs at least one)
 * @returns {boolean}
 */
export const canAccessFeature = (role, permissions) => {
  return hasPermission(role, permissions, false);
};

/**
 * Check if user can perform an action that requires all permissions (AND logic)
 * @param {string} role - User's role
 * @param {string[]} permissions - Array of permissions (user needs all)
 * @returns {boolean}
 */
export const canPerformAction = (role, permissions) => {
  return hasPermission(role, permissions, true);
};
